import { ref, computed, watch, effectScope } from 'vue'
import { getActivePinia } from 'pinia'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { FirebaseProvider } from '../infrastructure/providers/FirebaseProvider'
import type { FieldDefinition } from '../interfaces/fields-management'
import { getFieldDisplayLabel } from '../utils/fields/resolveFieldLabels'
import { useCompanyStore } from '../stores/company'
import { i18n } from '../config/i18n'
import {
  purchaseOrdersSystemFields,
  shipmentsSystemFields,
  quotesSystemFields,
  warehouseSystemFields
} from '../data/mockFieldsBank'

// Mapa de traducciones por nombre técnico — se construye una sola vez.
// Warehouse va primero: comparte nombres (status, createdAt…) y gana el último.
const fieldTranslationsMap = new Map<string, { labelES: string; labelEN: string }>()
for (const field of [...warehouseSystemFields, ...purchaseOrdersSystemFields, ...shipmentsSystemFields, ...quotesSystemFields]) {
  if (field.name) {
    fieldTranslationsMap.set(field.name, {
      labelES: field.labelES || field.label,
      labelEN: field.labelEN || ''
    })
  }
}

// Seeds del banco de campos por módulo, usados para agregar campos de sistema
// nuevos a empresas cuya config en Firestore fue guardada antes de que existieran
const seedFieldsByModule: Record<string, FieldDefinition[]> = {
  purchaseOrders: purchaseOrdersSystemFields,
  shipments: shipmentsSystemFields,
  quotes: quotesSystemFields,
  warehouse: warehouseSystemFields
}

function injectTranslations(field: FieldDefinition): FieldDefinition {
  if (field.labelES && field.labelEN) return field
  const translation = fieldTranslationsMap.get(field.name)
  if (translation) {
    return { ...field, labelES: field.labelES || translation.labelES, labelEN: field.labelEN || translation.labelEN }
  }
  return { ...field, labelES: field.labelES || field.label }
}

interface SystemFieldsConfig {
  companyId: string
  lastUpdated: string
  fields: FieldDefinition[]
}

// Estructura para mantener campos de múltiples módulos
interface ModuleFieldsCache {
  [moduleName: string]: FieldDefinition[]
}

// Estado compartido (singleton) - Una única instancia para todos los componentes
const isLoading = ref(false)
const error = ref<string | null>(null)
const systemFieldsConfig = ref<SystemFieldsConfig | null>(null)
const moduleFieldsCache = ref<ModuleFieldsCache>({})
// Cache de IDs de campos compartidos visibles por módulo (singleton)
const sharedFieldsVisibleIds = ref<Record<string, string[]>>({})
// Cache de visibilidad de tabs del detalle de embarque (singleton)
const tabVisibilityConfig = ref<Record<string, boolean> | null>(null)

// País de la compañía: algunos rótulos cambian por país (ver getFieldDisplayLabel)
function companyCountry(): string | undefined {
  return getActivePinia() ? useCompanyStore().currentCompanyData?.country : undefined
}

function enrichFieldWithLabel(field: FieldDefinition): FieldDefinition {
  return { ...field, label: getFieldDisplayLabel(field, companyCountry()) }
}

function reEnrichLabels() {
  const updatedCache: ModuleFieldsCache = {}
  for (const [mod, fields] of Object.entries(moduleFieldsCache.value)) {
    updatedCache[mod] = fields.map(enrichFieldWithLabel)
  }
  moduleFieldsCache.value = updatedCache
  if (systemFieldsConfig.value) {
    systemFieldsConfig.value = {
      ...systemFieldsConfig.value,
      fields: systemFieldsConfig.value.fields.map(enrichFieldWithLabel)
    }
  }
}

// Watch singleton: re-enriquecer labels cuando cambia el idioma
// Debe estar a nivel de módulo para no destruirse cuando un componente se desmonta
watch(() => i18n.global.locale.value, reEnrichLabels)

// El store de compañía solo existe con Pinia activo: este watch arranca en el primer
// uso del composable, en un scope desacoplado para que no muera con el componente
let countryWatchStarted = false
function startCountryWatch() {
  if (countryWatchStarted || !getActivePinia()) return
  countryWatchStarted = true
  effectScope(true).run(() => watch(companyCountry, reEnrichLabels))
}

/**
 * Función helper para limpiar valores undefined de un objeto
 * Firebase no acepta valores undefined
 */
function removeUndefinedValues(obj: any): any {
  if (obj === null || obj === undefined) return null
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item))
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {}
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value)
      }
    }
    return cleaned
  }
  
  return obj
}

/**
 * Composable para obtener la configuración de campos del sistema desde Firebase
 * Ruta: /companies/companySelected/config/systemFields
 * 
 * IMPORTANTE: Este composable usa un estado compartido (singleton) para que
 * todos los componentes que lo usen vean los mismos datos reactivos.
 */
export function useSystemFieldsConfig() {
  startCountryWatch()
  
  /**
   * Obtener configuración de campos del sistema desde Firebase
   * Ahora mantiene campos de múltiples módulos en caché
   */
  const loadSystemFieldsConfig = async (companyId: string, module: string): Promise<void> => {
    if (!companyId) {
      console.warn('No companyId provided for system fields config')
      return
    }

    try {
      isLoading.value = true
      error.value = null

      // Usar Firebase directamente
      const db = FirebaseProvider.getInstance().getFirestore()
      const systemFieldsRef = doc(db, 'Companies', companyId, 'config', 'systemFields')
      
      const configDoc = await getDoc(systemFieldsRef)

      if (configDoc.exists()) {
        const data = configDoc.data()
        
        // Obtener solo los campos del módulo solicitado. Warehouse no tenía config
        // antes de KILA-535: sin datos guardados arranca con los seeds de abajo.
        const moduleData = data[module] ?? (module === 'warehouse' ? [] : undefined)
        
        if (!moduleData) {
          console.warn(`No se encontraron campos para el módulo: ${module}`)
          moduleFieldsCache.value[module] = []
          updateSystemFieldsConfig(companyId, data.lastUpdated)
          return
        }
        
        let moduleFields: FieldDefinition[] = []
        
        // Manejar tanto arrays como objetos
        if (Array.isArray(moduleData)) {
          // Si es un array, usar directamente
          moduleFields = moduleData as FieldDefinition[]
        } else if (typeof moduleData === 'object' && moduleData !== null) {
          // Si es un objeto, convertir a array
          moduleFields = Object.values(moduleData as Record<string, any>)
            .filter(field => field && typeof field === 'object')
            .map(field => ({
              ...field,
              module: module // Asegurar que tenga el módulo correcto
            })) as FieldDefinition[]
        }
        
        // Deduplicar campos por ID y por name para evitar duplicados acumulados en Firestore
        const seenIds = new Set<string>()
        const seenNames = new Set<string>()
        moduleFields = moduleFields.filter(field => {
          const fieldId = field.id || field.name
          if (seenIds.has(fieldId)) return false
          seenIds.add(fieldId)
          // También deduplicar por name (puede haber campos con distinto id pero mismo name)
          const fieldName = field.name
          if (seenNames.has(fieldName)) return false
          seenNames.add(fieldName)
          return true
        })

        // Merge de seeds: agregar campos de sistema del banco que aún no existen
        // en la config guardada (p. ej. campos agregados en versiones nuevas de la app).
        // Se persisten la próxima vez que se guarde la config del módulo.
        for (const seedField of seedFieldsByModule[module] || []) {
          if (!seedField.isSystem) continue
          const seedId = seedField.id || seedField.name
          if (seenIds.has(seedId) || seenNames.has(seedField.name)) continue
          seenIds.add(seedId)
          seenNames.add(seedField.name)
          moduleFields.push({ ...seedField })
        }

        // Inyectar traducciones y resolver label según locale
        moduleFields = moduleFields.map(f => enrichFieldWithLabel(injectTranslations(f)))

        // Preservar campos compartidos (shared_) existentes en caché
        const existingShared = (moduleFieldsCache.value[module] || []).filter((f: any) => f.id.startsWith('shared_'))
        if (existingShared.length > 0) {
          moduleFields = [...moduleFields, ...existingShared]
        }

        // Guardar en caché (inmutable para reactividad)
        moduleFieldsCache.value = { ...moduleFieldsCache.value, [module]: moduleFields }

        // Extraer configuración de visibilidad de tabs si existe
        if (data.shipmentDetailTabs && typeof data.shipmentDetailTabs === 'object') {
          tabVisibilityConfig.value = data.shipmentDetailTabs
        }

        // Actualizar configuración global con todos los campos cargados
        updateSystemFieldsConfig(companyId, data.lastUpdated)
      } else {
        console.warn('No existe el documento de configuración de campos')
        moduleFieldsCache.value = { ...moduleFieldsCache.value, [module]: [] }
        systemFieldsConfig.value = null
      }
    } catch (err) {
      console.error('Error loading system fields config from Firebase:', err)
      error.value = err instanceof Error ? err.message : 'Error al cargar configuración de campos'
      moduleFieldsCache.value = { ...moduleFieldsCache.value, [module]: [] }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Actualiza la configuración global con todos los campos cargados en caché
   */
  function updateSystemFieldsConfig(companyId: string, lastUpdated?: string) {
    const allFields: FieldDefinition[] = []
    
    // Consolidar todos los campos de todos los módulos
    Object.entries(moduleFieldsCache.value).forEach(([, fields]) => {
      allFields.push(...fields)
    })
    
    systemFieldsConfig.value = {
      companyId,
      lastUpdated: lastUpdated || new Date().toISOString(),
      fields: allFields
    }
      }

  /**
   * Guardar configuración de campos del sistema en Firebase
   */
  const saveSystemFieldsConfig = async (
    companyId: string,
    module: string,
    fields: FieldDefinition[]
  ): Promise<{ success: boolean; error?: string }> => {
    if (!companyId || !fields) {
      return { success: false, error: 'Missing companyId or fields data' }
    }

    try {
      isLoading.value = true
      error.value = null

      // Excluir campos cross-module (virtuales) y compartidos antes de persistir
      const persistableFields = fields.filter(f => !f.isCrossModule && !f.id.startsWith('shared_'))

      const configData = {
        companyId,
        lastUpdated: new Date().toISOString(),
        [module]: persistableFields.map(field => ({
          ...field,
          // Asegurar que todos los campos tengan las propiedades necesarias
          visible: field.visible !== undefined ? field.visible : true,
          editable: field.editable !== undefined ? field.editable : true,
          required: field.required !== undefined ? field.required : false,
          order: field.order || 0
        }))
      }

      // Limpiar todos los valores undefined (Firebase no los acepta)
      const cleanedData = removeUndefinedValues(configData)

      // Usar Firebase directamente
      const db = FirebaseProvider.getInstance().getFirestore()
      const systemFieldsRef = doc(db, 'Companies', companyId, 'config', 'systemFields')
      
      // Usar merge: true para no sobrescribir otros módulos
      await setDoc(systemFieldsRef, cleanedData, { merge: true })

      // Recargar campos regulares desde Firebase
      const db2 = FirebaseProvider.getInstance().getFirestore()
      const reloadDoc = await getDoc(doc(db2, 'Companies', companyId, 'config', 'systemFields'))
      let reloadedFields: FieldDefinition[] = []
      if (reloadDoc.exists()) {
        const data = reloadDoc.data()
        const moduleData = data[module]
        if (Array.isArray(moduleData)) {
          reloadedFields = moduleData as FieldDefinition[]
        } else if (typeof moduleData === 'object' && moduleData !== null) {
          reloadedFields = Object.values(moduleData as Record<string, any>)
            .filter(f => f && typeof f === 'object')
            .map(f => ({ ...f, module })) as FieldDefinition[]
        }
      }

      // Preservar campos compartidos (shared_) que ya estaban en el caché
      const previousFields = moduleFieldsCache.value[module] || []
      const existingSharedFields = previousFields.filter(f => f.id.startsWith('shared_'))

      // Actualización ATÓMICA: campos regulares + shared en una sola escritura
      moduleFieldsCache.value = {
        ...moduleFieldsCache.value,
        [module]: [...reloadedFields, ...existingSharedFields]
      }
      updateSystemFieldsConfig(companyId)
      
      return { success: true }
    } catch (err) {
      console.error('Error saving system fields config to Firebase:', err)
      const errorMsg = err instanceof Error ? err.message : 'Error al guardar configuración'
      error.value = errorMsg
      return { success: false, error: errorMsg }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Obtener campos de un módulo específico desde el caché
   */
  const getModuleFields = computed(() => (module: string): FieldDefinition[] => {
    // Primero intentar desde el caché
    if (moduleFieldsCache.value[module]) {
      return moduleFieldsCache.value[module]
    }
    
    // Fallback: buscar en la configuración global
    if (!systemFieldsConfig.value?.fields) {
      return []
    }
    
    return systemFieldsConfig.value.fields.filter(field => field.module === module)
  })

  /**
   * Obtener campos por sección
   */
  const getSectionFields = computed(() => (module: string, section: string): FieldDefinition[] => {
    if (!systemFieldsConfig.value?.fields) {
      return []
    }
    
    return systemFieldsConfig.value.fields.filter(
      field => field.module === module && field.section === section
    )
  })

  /**
   * Verificar si la configuración está disponible
   */
  const hasConfig = computed(() => !!systemFieldsConfig.value?.fields?.length)

  /**
   * Obtener estadísticas de la configuración
   */
  const configStats = computed(() => {
    if (!systemFieldsConfig.value?.fields) {
      return {
        totalFields: 0,
        visibleFields: 0,
        hiddenFields: 0,
        editableFields: 0,
        requiredFields: 0
      }
    }

    const fields = systemFieldsConfig.value.fields
    return {
      totalFields: fields.length,
      visibleFields: fields.filter(f => f.visible).length,
      hiddenFields: fields.filter(f => !f.visible).length,
      editableFields: fields.filter(f => f.editable).length,
      requiredFields: fields.filter(f => f.required).length
    }
  })

  // Sistema antiguo de cross-module eliminado - ahora usamos sharedFields

  // sharedFieldsVisibleIds es singleton a nivel de módulo (línea 24)

  /**
   * Mapa de módulos relacionados para campos compartidos
   */
  const SHARED_MODULE_MAP: Record<string, string[]> = {
    shipments: ['purchaseOrders'],
    purchaseOrders: ['shipments'],
  }

  /**
   * Carga campos compartidos de otros módulos que incluyen al módulo actual en sharedWith.
   * Estos campos se inyectan al caché del módulo actual bajo la sección 'sharedFields'.
   */
  const loadSharedFields = async (companyId: string, module: string): Promise<void> => {
    const relatedModules = SHARED_MODULE_MAP[module]
    if (!relatedModules) return

    // Cargar módulos relacionados si no están en caché
    for (const relMod of relatedModules) {
      if (!moduleFieldsCache.value[relMod]?.length) {
        await loadSystemFieldsConfig(companyId, relMod)
      }
    }

    // Buscar campos con scope='shared' y sharedWith includes module
    const sharedFields: FieldDefinition[] = []

    for (const relMod of relatedModules) {
      const relFields = moduleFieldsCache.value[relMod] || []
      const shared = relFields.filter(f =>
        f.scope === 'shared' &&
        f.sharedWith?.includes(module as any) &&
        !f.isCrossModule
      )

      for (const field of shared) {
        sharedFields.push({
          ...field,
          id: `shared_${relMod}_${field.id}`,
          name: field.name,
          section: 'sharedFields',
          module: module as any,
          sourceModule: relMod as any,
          isCrossModule: false,
          isSystem: true,
          isCustom: false,
          editable: true,
          required: false,
          visible: false,
        })
      }
    }

    if (sharedFields.length === 0) return

    // Cargar visibilidad persistida desde Firebase
    const visibleKey = `${module}_sharedFieldsVisible`
    try {
      const db = FirebaseProvider.getInstance().getFirestore()
      const configDoc = await getDoc(doc(db, 'Companies', companyId, 'config', 'systemFields'))
      if (configDoc.exists()) {
        const data = configDoc.data()
        const visibleIds: string[] = data[visibleKey] || []
        sharedFieldsVisibleIds.value = { ...sharedFieldsVisibleIds.value, [module]: visibleIds }
        const visibleSet = new Set(visibleIds)
        sharedFields.forEach(f => { f.visible = visibleSet.has(f.id) })
      }
    } catch (err) {
      console.error('Error loading shared fields visibility:', err)
    }

    // Inyectar al caché del módulo actual sin duplicar
    const currentFields = moduleFieldsCache.value[module] || []
    const filtered = currentFields.filter(f => !f.id.startsWith('shared_'))
    moduleFieldsCache.value = {
      ...moduleFieldsCache.value,
      [module]: [...filtered, ...sharedFields]
    }

    updateSystemFieldsConfig(companyId)
  }

  /**
   * Guardar los IDs de campos compartidos visibles en Firebase.
   */
  const saveSharedFieldsVisibility = async (
    companyId: string,
    module: string,
    visibleIds: string[]
  ): Promise<void> => {
    const visibleKey = `${module}_sharedFieldsVisible`
    sharedFieldsVisibleIds.value = { ...sharedFieldsVisibleIds.value, [module]: visibleIds }

    // Actualizar caché síncrono
    const currentFields = moduleFieldsCache.value[module] || []
    const visibleSet = new Set(visibleIds)
    moduleFieldsCache.value = {
      ...moduleFieldsCache.value,
      [module]: currentFields.map(f =>
        f.id.startsWith('shared_') ? { ...f, visible: visibleSet.has(f.id) } : f
      )
    }

    // Persistir en Firebase
    try {
      const db = FirebaseProvider.getInstance().getFirestore()
      const systemFieldsRef = doc(db, 'Companies', companyId, 'config', 'systemFields')
      await setDoc(systemFieldsRef, { [visibleKey]: visibleIds }, { merge: true })
    } catch (err) {
      console.error('Error saving shared fields visibility:', err)
    }
  }

  /**
   * Actualizar visibilidad de campos específicos según modo de transporte
   * @param companyId - ID de la compañía
   * @param module - Módulo (ej: 'shipments')
   * @param transportMode - Modo de transporte ('Air Transport' | 'Ocean Transport' | 'Ground Transport')
   */
  const updateFieldsVisibilityByTransportMode = async (
    companyId: string,
    module: string,
    transportMode: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      isLoading.value = true
      
      // Definir qué campos mostrar/ocultar según modo
      const fieldsToUpdate: Record<string, boolean> = {}
      
      if (transportMode === 'Air Transport') {
        fieldsToUpdate['hawb'] = true
        fieldsToUpdate['mawb'] = true
        fieldsToUpdate['hbl'] = false
        fieldsToUpdate['mbl'] = false
        fieldsToUpdate['cpi'] = false
      } else if (transportMode === 'Ocean Transport') {
        fieldsToUpdate['hbl'] = true
        fieldsToUpdate['mbl'] = true
        fieldsToUpdate['hawb'] = false
        fieldsToUpdate['mawb'] = false
        fieldsToUpdate['cpi'] = false
      } else if (transportMode === 'Ground Transport') {
        fieldsToUpdate['cpi'] = true
        fieldsToUpdate['hawb'] = false
        fieldsToUpdate['mawb'] = false
        fieldsToUpdate['hbl'] = false
        fieldsToUpdate['mbl'] = false
      } else {
        fieldsToUpdate['hawb'] = false
        fieldsToUpdate['mawb'] = false
        fieldsToUpdate['hbl'] = false
        fieldsToUpdate['mbl'] = false
        fieldsToUpdate['cpi'] = false
      }
      
      
      // Obtener campos actuales del módulo desde Firebase
      const db = FirebaseProvider.getInstance().getFirestore()
      const systemFieldsRef = doc(db, 'Companies', companyId, 'config', 'systemFields')
      const configDoc = await getDoc(systemFieldsRef)
      
      if (!configDoc.exists()) {
        throw new Error('No existe configuración de campos')
      }
      
      const data = configDoc.data()
      let moduleData = data[module]
      
      if (!moduleData) {
        throw new Error(`No hay datos para el módulo: ${module}`)
      }
      
      // Convertir a array si es necesario
      let fieldsArray: FieldDefinition[] = []
      if (Array.isArray(moduleData)) {
        fieldsArray = [...moduleData]
      } else if (typeof moduleData === 'object') {
        fieldsArray = Object.values(moduleData).filter(f => f && typeof f === 'object') as FieldDefinition[]
      }
      
      // Actualizar visibilidad de los campos especificados
      let hasChanges = false
      fieldsArray = fieldsArray.map(field => {
        const fieldId = field.id || field.name
        if (fieldId && fieldsToUpdate.hasOwnProperty(fieldId)) {
          const newVisibility = fieldsToUpdate[fieldId]
          if (field.visible !== newVisibility) {
            hasChanges = true
            return { ...field, visible: newVisibility }
          }
        }
        return field
      })
      
      if (!hasChanges) {
        return { success: true }
      }
      
      // Guardar en Firebase
      const cleanedFields = removeUndefinedValues(fieldsArray)
      await setDoc(systemFieldsRef, { [module]: cleanedFields }, { merge: true })
      
      // Actualizar caché local
      const existingShared = (moduleFieldsCache.value[module] || []).filter((f: any) => f.id.startsWith('shared_'))
      
      moduleFieldsCache.value = {
        ...moduleFieldsCache.value,
        [module]: [...fieldsArray, ...existingShared]
      }
      
      updateSystemFieldsConfig(companyId)
      
      return { success: true }
      
    } catch (err) {
      console.error('❌ Error actualizando visibilidad de campos:', err)
      const errorMsg = err instanceof Error ? err.message : 'Error desconocido'
      error.value = errorMsg
      return { success: false, error: errorMsg }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Cargar configuración de visibilidad de tabs del detalle de embarque
   */
  const loadTabVisibilityConfig = async (companyId: string): Promise<void> => {
    if (!companyId) return
    try {
      const db = FirebaseProvider.getInstance().getFirestore()
      const configDoc = await getDoc(doc(db, 'Companies', companyId, 'config', 'systemFields'))
      if (configDoc.exists()) {
        const data = configDoc.data()
        if (data.shipmentDetailTabs && typeof data.shipmentDetailTabs === 'object') {
          tabVisibilityConfig.value = data.shipmentDetailTabs
        }
      }
    } catch (err) {
      console.error('Error loading tab visibility config:', err)
    }
  }

  /**
   * Guardar configuración de visibilidad de tabs del detalle de embarque
   */
  const saveTabVisibilityConfig = async (
    companyId: string,
    config: Record<string, boolean>
  ): Promise<void> => {
    const db = FirebaseProvider.getInstance().getFirestore()
    const systemFieldsRef = doc(db, 'Companies', companyId, 'config', 'systemFields')
    await setDoc(systemFieldsRef, { shipmentDetailTabs: config }, { merge: true })
    tabVisibilityConfig.value = { ...config }
  }

  /**
   * Limpiar el caché de un módulo específico o de todos
   */
  function clearCache(module?: string) {
    if (module) {
      const { [module]: _, ...rest } = moduleFieldsCache.value
      moduleFieldsCache.value = rest
    } else {
      moduleFieldsCache.value = {}
    }
  }

  return {
    // Estado
    systemFieldsConfig,
    isLoading,
    error,
    moduleFieldsCache,
    
    // Getters
    getModuleFields,
    getSectionFields,
    hasConfig,
    configStats,
    
    // Acciones
    loadSystemFieldsConfig,
    saveSystemFieldsConfig,
    updateFieldsVisibilityByTransportMode,
    loadSharedFields,
    saveSharedFieldsVisibility,
    sharedFieldsVisibleIds,
    clearCache,
    tabVisibilityConfig,
    loadTabVisibilityConfig,
    saveTabVisibilityConfig
  }
}
